import { ErrorTranslation } from '@surface-elements/shared/util-error';

/**
 * Account-specific error translations based on actual API responses.
 * These override base translations when dealing with account-related endpoints.
 */
export const ACCOUNT_ERROR_TRANSLATIONS: ErrorTranslation[] = [
  // Specific API error: "accountId not found!"
  {
    code: 'accounts.not_found',
    message:
      'The account could not be found. It may have been deleted or you may not have permission to view it.',
    title: 'Account Not Found',
    matcher: error =>
      error.status === 404 && error.error?.message === 'accountId not found!',
    priority: 10,
  },

  // Duplicate account name error
  {
    code: 'accounts.duplicate_name',
    message:
      'An account with this name already exists. Please choose a different name.',
    title: 'Duplicate Account Name',
    matcher: error =>
      error.error?.message === 'duplicate' && error.url?.includes('/accounts'),
    priority: 10,
  },

  // Generic 404 on accounts endpoint
  {
    code: 'accounts.resource_not_found',
    message: 'The requested account resource could not be found.',
    title: 'Not Found',
    matcher: error => error.status === 404 && error.url?.includes('/accounts'),
    priority: 5,
  },

  // Account contact creation errors
  {
    code: 'accounts.contact_creation_failed',
    message:
      'Unable to add the contact to this account. Please verify the contact information and try again.',
    title: 'Contact Creation Failed',
    matcher: error =>
      error.status >= 400 &&
      error.url?.includes('/accounts/') &&
      error.url?.includes('/contacts'),
    priority: 7,
  },

  // 400 errors on accounts endpoint
  {
    code: 'accounts.bad_request',
    message:
      'The account request could not be processed. Please check your input and try again.',
    title: 'Invalid Account Request',
    matcher: error => error.status === 400 && error.url?.includes('/accounts'),
    priority: 5,
  },

  // Validation errors for accounts
  {
    code: 'accounts.validation_error',
    message:
      'The account information provided is invalid. Please check all required fields are filled correctly.',
    title: 'Validation Error',
    matcher: error => error.status === 422 && error.url?.includes('/accounts'),
    priority: 6,
  },

  // 500 errors on accounts endpoint
  {
    code: 'accounts.server_error',
    message:
      'Unable to process your account request. Our team has been notified. Please try again later.',
    title: 'Account Processing Error',
    matcher: error => error.status === 500 && error.url?.includes('/accounts'),
    priority: 5,
  },

  // Account creation specific
  {
    code: 'accounts.creation_failed',
    message:
      'Unable to create the account. Please verify all required information is provided and the account name is unique.',
    title: 'Account Creation Failed',
    matcher: error =>
      error.status >= 400 &&
      error.url?.includes('/accounts') &&
      error.url?.endsWith('/accounts'), // POST to /accounts
    priority: 6,
  },

  // Account update specific
  {
    code: 'accounts.update_failed',
    message:
      'Unable to update the account. The account may have been modified by another user.',
    title: 'Account Update Failed',
    matcher: error =>
      error.status >= 400 &&
      error.url?.includes('/accounts/') &&
      !error.url?.includes('/contacts'), // PATCH to /accounts/:id
    priority: 6,
  },

  // Account deletion
  {
    code: 'accounts.deletion_failed',
    message:
      'Unable to delete the account. The account may have associated jobs or orders that must be removed first.',
    title: 'Account Deletion Failed',
    matcher: error =>
      error.status >= 400 &&
      error.url?.includes('/accounts/') &&
      error.headers?.get('method') === 'DELETE',
    priority: 6,
  },
];

/**
 * Provider configuration for account error translations
 */
export const ACCOUNT_ERROR_PROVIDERS = [
  {
    provide: 'ERROR_TRANSLATION_CONFIG',
    useValue: {
      translations: ACCOUNT_ERROR_TRANSLATIONS,
      appName: 'accounts-domain',
    },
    multi: true,
  },
];
